import { useState } from 'react';
import { Check, ExternalLink, Loader2, Clock } from 'lucide-react';

import { TaskProgress } from '../types';

import { TweetVerificationModal } from './TweetVerificationModal';

import { useCompleteTask } from '@/hooks/useNewYearCampaign';

interface TaskCardProps {
  task: TaskProgress;
}

export function TaskCard({ task }: TaskCardProps) {
  const [showTweetModal, setShowTweetModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate: completeTask, isPending } = useCompleteTask();

  // Combined loading state - prevents race condition from rapid clicks
  const isLoading = isSubmitting || isPending;

  const handleComplete = () => {
    // Prevent double-clicks
    if (isLoading) return;

    // Open tweet modal for both 'tweet' and 'manual' verification types
    if (
      task.requiresVerification &&
      (task.verificationType === 'tweet' || task.verificationType === 'manual')
    ) {
      setShowTweetModal(true);
    } else {
      setIsSubmitting(true);
      completeTask(
        { taskType: task.type },
        {
          onSettled: () => setIsSubmitting(false),
        }
      );
    }
  };

  const handleTweetVerified = (tweetUrl: string, originalTweetUrl?: string) => {
    setIsSubmitting(true);
    completeTask(
      { taskType: task.type, tweetUrl, originalTweetUrl },
      {
        onSettled: () => setIsSubmitting(false),
      }
    );
    setShowTweetModal(false);
  };

  const progressPercent =
    task.targetProgress > 0
      ? Math.min((task.currentProgress / task.targetProgress) * 100, 100)
      : task.isCompleted
      ? 100
      : 0;

  return (
    <>
      <div
        className={`bg-gray-800/50 rounded-xl p-4 border transition-all ${
          task.isCompleted
            ? 'border-green-500/30 opacity-60'
            : task.pendingReview
            ? 'border-amber-500/30'
            : task.canComplete
            ? 'border-cyan-500/30 hover:border-cyan-500/50'
            : 'border-gray-700'
        }`}
      >
        <div className="flex items-start gap-4">
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-semibold truncate">
                {task.name}
              </h3>
              {task.requiresVerification && (
                <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
              )}
            </div>

            {task.description && (
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                {task.description}
              </p>
            )}

            {/* Progress bar for threshold tasks */}
            {task.targetProgress > 1 && !task.isCompleted && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>
                    {task.currentProgress} / {task.targetProgress}
                  </span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Completion count for repeatable tasks */}
            {task.maxCompletions > 1 && (
              <p className="text-xs text-gray-500">
                Completed {task.currentCompletions} / {task.maxCompletions} times
              </p>
            )}
          </div>

          {/* Points and action */}
          <div className="flex flex-col items-end gap-2">
            <span className="text-xl font-bold text-cyan-400">
              +{task.points}
            </span>

            <button
              onClick={handleComplete}
              disabled={!task.canComplete || isLoading || task.isCompleted || task.pendingReview}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                task.isCompleted
                  ? 'bg-green-500/20 text-green-400 cursor-default'
                  : task.pendingReview
                  ? 'bg-amber-500/20 text-amber-400 cursor-default'
                  : task.canComplete && !isLoading
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : task.isCompleted ? (
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Done
                </span>
              ) : task.pendingReview ? (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Pending
                </span>
              ) : task.canComplete ? (
                'Complete'
              ) : (
                'Locked'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tweet verification modal */}
      {showTweetModal && (
        <TweetVerificationModal
          task={task}
          onVerify={handleTweetVerified}
          onClose={() => setShowTweetModal(false)}
        />
      )}
    </>
  );
}
